import { Component, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UtilisateurService } from '../../../_services/utilisateur/utilisateur.service';
import { Utilisateur } from '../../../_interfaces/utilisateur';
import { EvenementService } from '../../../_services/evenement/evenement.service';
import { Evenement } from '../../../_interfaces/evenement';
import { InscriptionService } from '../../../_services/inscription/inscription.service';
import { AuthService } from '../../../_services/auth/auth.service';
import { ToastComponent } from '../../../_components/toast/toast.component';
import { AdminPageHeaderComponent } from '../../../_components/admin-page-header/admin-page-header.component';

type SortKey = 'lastName' | 'firstName' | 'email' | 'role';
type PendingActionKind = 'promote' | 'demote' | 'disable' | 'enable';

interface PendingAction {
  utilisateur: Utilisateur;
  kind: PendingActionKind;
}

@Component({
    selector: 'app-utilisateur-edition',
    imports: [FormsModule, ReactiveFormsModule, ToastComponent, AdminPageHeaderComponent],
    templateUrl: './utilisateur-edition.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './utilisateur-edition.component.scss'
})
export class UtilisateurEditionComponent {
  private utilisateurService = inject(UtilisateurService);
  private evenementService = inject(EvenementService);
  private inscriptionService = inject(InscriptionService);
  private authService = inject(AuthService);
  private formBuilder = inject(FormBuilder);

  @ViewChild(ToastComponent) toast!: ToastComponent;

  utilisateurs: Utilisateur[] = [];
  evenements: Evenement[] = [];
  currentUid: string | null = null;

  inscriptionForm: FormGroup;
  isInscriptionModalOpen = false;
  isSubmitting = false;

  isConfirmModalOpen = false;
  pendingAction: PendingAction | null = null;
  isConfirming = false;

  search = '';
  sortKey: SortKey = 'lastName';
  sortAsc = true;

  constructor() {
    this.inscriptionForm = this.formBuilder.group({
      evenement_key: ['', Validators.required],
      utilisateur_uid: ['', Validators.required],
      is_present: [true],
    });

    this.authService.currentUid$.subscribe(uid => this.currentUid = uid ?? null);

    this.loadUtilisateurs();
    this.loadEvenements();
  }

  private loadUtilisateurs() {
    this.utilisateurService.readAllUtilisateurs().subscribe(utilisateurs => {
      this.utilisateurs = utilisateurs;
    });
  }

  private loadEvenements() {
    this.evenementService.readAllFutureEvenements().subscribe(evenements => {
      this.evenements = evenements;
    });
  }

  get filteredUtilisateurs(): Utilisateur[] {
    const q = this.search.trim().toLowerCase();
    let list = this.utilisateurs;
    if (q) {
      list = list.filter(u =>
        (u.lastName || '').toLowerCase().includes(q) ||
        (u.firstName || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...list].sort((a, b) => {
      const av = (a[this.sortKey] || '').toString().toLowerCase();
      const bv = (b[this.sortKey] || '').toString().toLowerCase();
      return this.sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return sorted;
  }

  get adminCount(): number {
    return this.utilisateurs.filter(u => u.role === 'admin').length;
  }

  toggleSort(key: SortKey) {
    if (this.sortKey === key) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortKey = key;
      this.sortAsc = true;
    }
  }

  openInscriptionModal() {
    this.inscriptionForm.reset({ is_present: true });
    this.isInscriptionModalOpen = true;
  }

  closeInscriptionModal() {
    this.isInscriptionModalOpen = false;
  }

  validate() {
    if (this.inscriptionForm.invalid || this.isSubmitting) return;
    this.isSubmitting = true;
    this.inscriptionService.createInscription(
      this.inscriptionForm.value.utilisateur_uid,
      this.inscriptionForm.value.evenement_key,
      !!this.inscriptionForm.value.is_present
    ).then(
      () => {
        this.showToast('Inscription enregistrée', 'success');
        this.inscriptionForm.reset({ is_present: true });
        this.closeInscriptionModal();
      },
      () => this.showToast("Erreur lors de l'inscription", 'error')
    ).finally(() => this.isSubmitting = false);
  }

  initials(u: Utilisateur): string {
    return ((u.firstName?.[0] || '') + (u.lastName?.[0] || '')).toUpperCase() || '?';
  }

  isSelf(u: Utilisateur): boolean {
    return !!this.currentUid && u.uid === this.currentUid;
  }

  requestToggleRole(u: Utilisateur) {
    if (this.isSelf(u)) return;
    this.pendingAction = { utilisateur: u, kind: u.role === 'admin' ? 'demote' : 'promote' };
    this.isConfirmModalOpen = true;
  }

  requestToggleDisabled(u: Utilisateur) {
    if (this.isSelf(u)) return;
    this.pendingAction = { utilisateur: u, kind: u.disabled ? 'enable' : 'disable' };
    this.isConfirmModalOpen = true;
  }

  closeConfirmModal() {
    this.isConfirmModalOpen = false;
    this.pendingAction = null;
  }

  get confirmModalTitle(): string {
    switch (this.pendingAction?.kind) {
      case 'promote': return 'Promouvoir administrateur';
      case 'demote': return 'Retirer les droits admin';
      case 'disable': return 'Désactiver le compte';
      case 'enable': return 'Réactiver le compte';
      default: return '';
    }
  }

  get confirmModalMessage(): string {
    const nom = this.pendingAction ? `${this.pendingAction.utilisateur.firstName} ${this.pendingAction.utilisateur.lastName}` : '';
    switch (this.pendingAction?.kind) {
      case 'promote':
        return `${nom} pourra accéder au back-office et gérer l'ensemble du site.`;
      case 'demote':
        return `${nom} perdra l'accès au back-office.`;
      case 'disable':
        return `${nom} ne pourra plus se connecter ni s'inscrire à de nouveaux événements.`;
      case 'enable':
        return `${nom} pourra de nouveau se connecter et s'inscrire aux événements.`;
      default:
        return '';
    }
  }

  confirmPendingAction() {
    if (!this.pendingAction || this.isConfirming) return;
    const { utilisateur, kind } = this.pendingAction;
    this.isConfirming = true;

    let action: Promise<void>;
    let successMessage: string;
    if (kind === 'promote' || kind === 'demote') {
      const role = kind === 'promote' ? 'admin' : 'user';
      action = this.utilisateurService.updateRole(utilisateur.uid, role);
      successMessage = kind === 'promote' ? 'Utilisateur promu administrateur' : "Droits admin retirés";
    } else {
      const disabled = kind === 'disable';
      action = this.utilisateurService.setDisabled(utilisateur.uid, disabled);
      successMessage = disabled ? 'Compte désactivé' : 'Compte réactivé';
    }

    action.then(
      () => {
        this.showToast(successMessage, 'success');
        this.closeConfirmModal();
      },
      () => this.showToast("Une erreur est survenue", 'error')
    ).finally(() => this.isConfirming = false);
  }

  showToast(str: string, type: 'success' | 'error' | 'info' = 'info') {
    this.toast?.showToast(str, type);
  }
}
